import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { useAppSettings } from "@redux/slices/globalSlice";
import { setLoginModalType } from "@redux/slices/appStateSlice";

type LoginModalVariant = "default" | "go-back";

/**
 * Centralized guard for apps that need to remain private despite the public
 * user switch being enabled.  `restrict_public_login` in global app_settings
 * signals that guest users should be blocked from all screens except PDP
 * (item-detail) deeplinks, which are required for barcode-scanner flows.
 *
 * Uses the client-side `isGuest` flag (set only for `public_token` auth) so
 * that iframe, SSO, and auth_id users are never affected.
 *
 * Returns `shouldBlock` – when `true` the caller must render an empty view
 * (the hook already dispatches the login-modal action).
 */
export const usePrivateAppGuard = (
  modalVariant: LoginModalVariant = "default"
) => {
  const dispatch = useDispatch();
  const isFocused = useIsFocused();
  const getAppSettings = useAppSettings();
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);

  const restrictPublicLogin = !!(
    getAppSettings && getAppSettings?.restrict_public_login
  );
  const shouldBlock = isGuest && restrictPublicLogin;

  useEffect(() => {
    if (shouldBlock && isFocused) {
      dispatch(setLoginModalType(modalVariant));
    }
  }, [shouldBlock, isFocused, modalVariant, dispatch]);

  return {
    shouldBlock,
    isGuest,
    restrictPublicLogin,
  };
};

/**
 * Lightweight selector for components that only need to read the flag without
 * triggering the login-modal side-effect (e.g. Header, AuthScreen, PDP).
 *
 * Uses `isGuest` so iframe / SSO / auth_id users are never impacted.
 */
export const useIsPublicAccessRestricted = () => {
  const getAppSettings = useAppSettings();
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);

  const restrictPublicLogin = !!(
    getAppSettings && getAppSettings?.restrict_public_login
  );

  return {
    isGuest,
    restrictPublicLogin,
    shouldHide: isGuest && restrictPublicLogin,
  };
};
